"use client";

import { useEffect, useMemo, useState } from "react";
import { ProductCard } from "@/components/ProductCard";
import type { HomeProductShelf } from "@/lib/home-merchandising";
import { fixMaybeBrokenText } from "@/lib/text-client";

type HomeProductShelvesTabsProps = {
  shelves: HomeProductShelf[];
  showRetailPurchase?: boolean;
};

export function HomeProductShelvesTabs({
  shelves,
  showRetailPurchase = true
}: HomeProductShelvesTabsProps) {
  const visibleShelves = useMemo(
    () =>
      shelves
        .filter((shelf) => shelf.products.length > 0)
        .map((shelf) => ({ ...shelf, title: fixMaybeBrokenText(shelf.title) })),
    [shelves]
  );
  const [activeId, setActiveId] = useState(visibleShelves[0]?.id ?? "");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(max-width: 860px)");
    const sync = () => setIsMobile(media.matches);

    sync();
    media.addEventListener("change", sync);

    return () => media.removeEventListener("change", sync);
  }, []);

  const activeShelf = visibleShelves.find((shelf) => shelf.id === activeId) ?? visibleShelves[0] ?? null;

  if (!visibleShelves.length || !activeShelf) {
    return null;
  }

  const visibleProducts = activeShelf.products.slice(
    0,
    isMobile ? activeShelf.mobileLimit || activeShelf.limit : activeShelf.limit
  );

  return (
    <section className="home-shelves-v6" aria-labelledby="home-shelves-title">
      <div
        aria-label="Подборки товаров главной страницы"
        className="home-shelves-v6__tabs"
        role="tablist"
      >
        {visibleShelves.map((shelf) => {
          const isActive = shelf.id === activeShelf.id;
          return (
            <button
              aria-selected={isActive}
              className={`home-shelves-v6__tab ${isActive ? "is-active" : ""}`.trim()}
              key={shelf.id}
              onClick={() => setActiveId(shelf.id)}
              role="tab"
              type="button"
            >
              {shelf.title}
            </button>
          );
        })}
      </div>

      <div className="home-shelves-v6__panel" role="tabpanel">
        <div className="product-grid product-grid-retail home-shelves-v6__grid">
          {visibleProducts.map((product) => (
            <ProductCard
              key={`${activeShelf.id}-${product.slug}`}
              product={product}
              showRetailPurchase={showRetailPurchase}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
